import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc';

interface Product {
  productId: number;
  name: string;
  mainImage: string;
}

interface Category {
  productCatId: number;
  name: string;
  products: { productId: number }[];
}

const CATEGORY_API_URL = '/api/admin/shoppingcart/categories';
const PRODUCT_API_URL = '/api/admin/shoppingcart/products';

/**
 * This is a "forms lib" interfaced field for future support.
 *
 * This has not been tested with "forms lib" so it may not be fully compatible, or it may be missing features.
 */
const SortAllProductsFormsField = ({
  value,
  onValueChange,
}: {
  value: number[];
  onValueChange: (newValue: number[]) => void;
}) => {
  const [products, setProducts] = useState<Product[]>(null);

  useEffect(() => {
    (async () => {
      const categoryResponse = await axios.get<any, { data: Category[] }>(CATEGORY_API_URL);
      const productResponse = await axios.get<any, { data: Product[] }>(PRODUCT_API_URL);
      const categories = categoryResponse.data;
      const allProductsCategory = categories.find((category) => category.name === 'All Products');
      const allProductsProductIds = allProductsCategory.products.map((product) => product.productId);
      let products = productResponse.data;
      products = products.filter((product) => allProductsProductIds.includes(product.productId));
      products = products.sort((a: Product, b: Product) => {
        let rankA = value.indexOf(a.productId);
        let rankB = value.indexOf(b.productId);
        if (rankA === -1) {
          rankA = Infinity; // Unranked items go to bottom
        }
        if (rankB === -1) {
          rankB = Infinity; // Unranked items go to bottom
        }
        return rankA - rankB;
      });
      setProducts(products);
    })();
  }, []);

  const handleSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      const newProducts = arrayMove(products, oldIndex, newIndex);
      setProducts(newProducts);
      onValueChange(newProducts.map((product) => product.productId));
    },
    [products, onValueChange]
  );

  if (products === null) {
    return <div>Loading...</div>;
  }

  return <SortableList items={products} onSortEnd={handleSortEnd} />;
};

export default SortAllProductsFormsField;

interface SortableItemProps {
  value: Product;
}

const SortableItem = SortableElement<SortableItemProps>(({ value }) => (
  <div
    style={{
      backgroundColor: '#d9edf7',
      border: '1px solid black',
      cursor: 'pointer',
      margin: '2px 0',
      userSelect: 'none',
      zIndex: 1000000000,
    }}
  >
    <span style={{ width: '3em', display: 'inline-block' }}>
      <img src={value.mainImage} style={{ maxHeight: '2em', maxWidth: '2em' }} />
    </span>
    <span>{value.name}</span>
  </div>
));

interface SortableListProps {
  items: any[];
}

const SortableList = SortableContainer<SortableListProps>(({ items }) => {
  return (
    <div style={{ border: '1px solid grey', padding: '3px', margin: '0 3px 0 3px' }}>
      {items.map((value, index) => (
        <SortableItem key={`item-${index}`} index={index} value={value} />
      ))}
    </div>
  );
});
